// @react
import React from 'react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
// @common
import usePagination from 'modules/common/hooks/usePagination'
import config from 'constants/config'
// @libraries
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Button from '@bit/atd.web.button'
import Icon from '@bit/atd.web.icons'
import Spacer from '@bit/atd.web.spacer'
import icons from '@bit/atd.web.icons/getIcons'
import Grid from '@bit/atd.web.grid'
import ContentCard from 'modules/common/components/ContentCard'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import QueryError from 'modules/common/components/Errors/QueryError'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import PaginationScrollWrapper from 'modules/common/components/PaginationScrollWrapper'
// @graphql
import { useQuery } from '@apollo/client'
import {
  FilterOrder,
  GetUserContentQuery,
  GetUserContentQueryVariables,
  IAbstractSearchItem,
  UserContentOrderByList,
  UserContentRelationship,
} from 'lib/graphql/graphqlTypes'
import { GET_USER_CONTENT } from 'graphql/queries/userContent.query'
import strings from 'lib/constants/strings'

/**
 * 4.0.2.1 My ATD - Activity History - Bookmarks - Content
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 * fields:
 * Bookmark	id
 * Bookmark	title
 * Bookmark	contentTypes
 * Bookmark	imageUrl
 * Bookmark	url
 * Bookmark	description
 * Bookmark	date published
 * Bookmark author
 *
 */
const useStyles = makeStyles({
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
})

type PropsType = {
  setCount: Dispatch<SetStateAction<number>>
}

const BookmarksContent = ({ setCount }: PropsType) => {
  /******************************************
   * STATE
   ******************************************/

  const { Pagination, paginationProps } = usePagination()
  const [sortOrder, setSortOrder] = useState<FilterOrder>(FilterOrder.ASC)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserContentQuery,
    GetUserContentQueryVariables
  >(GET_USER_CONTENT, {
    variables: {
      input: {
        page: paginationProps.currentPage,
        pageSize: config.pageSize,
        filters: {
          userRelationship: UserContentRelationship.BOOKMARKED,
        },
        orderBy: {
          order: sortOrder,
          value: UserContentOrderByList.PUBLISHED_DATE,
        },
      },
    },
    fetchPolicy: 'network-only',
  })

  const count = data?.userContent?.meta.count
  useEffect(() => {
    if (count) {
      setCount(count)
    }
  }, [count])

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   */
  const toggleSortHandler = () => {
    const newOrder =
      sortOrder === FilterOrder.ASC ? FilterOrder.DESC : FilterOrder.ASC
    setSortOrder(newOrder)
  }

  const getOrderGlyph = (): keyof typeof icons => {
    return sortOrder === FilterOrder.ASC ? 'caret-down' : 'caret-up'
  }

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  return (
    <ConditionalLoader condition={!isLoading}>
      {data?.userContent?.meta.count ? (
        <PaginationScrollWrapper {...paginationProps}>
          <Grid container>
            <Grid item>
              <span>Sort by: </span>
              <Button
                onClick={toggleSortHandler}
                variant="secondary"
                size="small"
              >
                <>
                  Date: {sortOrder === FilterOrder.ASC ? 'Newest' : 'Oldest'}
                  <Spacer size={4} />
                  <Icon glyph={getOrderGlyph()} size={16} />
                </>
              </Button>
            </Grid>
          </Grid>

          <Spacer fullWidth size={30} />
          <Grid container spacing={3}>
            {data?.userContent?.data.map((item) => {
              return (
                <Grid
                  key={`${item.__typename}_${item.id}`}
                  item
                  xs={12}
                  className={'hello'}
                >
                  <ContentCard
                    typename={item.__typename}
                    item={item as IAbstractSearchItem}
                  />
                </Grid>
              )
            })}
          </Grid>

          <PaginationWrapper>
            <Pagination
              {...paginationProps}
              count={data?.userContent?.meta.totalPages}
            />
          </PaginationWrapper>
        </PaginationScrollWrapper>
      ) : (
        <div>
          <Typography component={'h3'} className={classes.textTitle}>
            {strings.ACTIVITY_HISTORY.BOOKMARKS.NO_RESULTS_MESSAGE}
          </Typography>
          <Typography component={'p'} className={classes.textDescription}>
            {strings.ACTIVITY_HISTORY.BOOKMARKS.NO_RESULTS_DESCRIPTION}
          </Typography>
        </div>
      )}
    </ConditionalLoader>
  )
}

export default BookmarksContent
